import { Suspense } from "react";
import { Link } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, InputWithLabel, Select } from "@ptsd/uikit";
import { ArrowLeft } from "@tamagui/lucide-icons";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { Form, Text, useMedia, View, XStack } from "tamagui";
import { z } from "zod";

import { api } from "~/utils/api";
import { LoadingScreen } from "~/component/blocks";
import { useAutoResetState } from "~/hooks";

const profileFormSchema = z.object({
  name: z.string().min(1),
  socDemGender: z.string().min(1).optional(),
  socDemAge: z.string().min(1).optional(),
  socDemArea: z.string().min(1).optional(),
  socDemEdu: z.string().min(1).optional(),
  socDemMaritalStatus: z.string().min(1).optional(),
  //  socDemPsychoterapiaExp: z.string().min(1).optional(),
});

type ProfileFormType = z.infer<typeof profileFormSchema>;

const Profile = () => {
  return (
    <Suspense fallback={<LoadingScreen height={"70dvh"} />}>
      <ProfileBody />
    </Suspense>
  );
};

export default Profile;

const ProfileBody = () => {
  const { sm } = useMedia();
  const apiUtils = api.useUtils();

  const [config] = api.patient.profileFormConfig.useSuspenseQuery(undefined);
  const [me] = api.patient.profile.useSuspenseQuery(undefined, {
    refetchOnMount: true,
  });

  const { control, handleSubmit, formState, reset } = useForm<ProfileFormType>({
    defaultValues: me,
    resolver: zodResolver(profileFormSchema),
  });

  const { currentState, updateState } = useAutoResetState(
    {
      default: "Зберегти зміни",
      isDone: "Зміни збережено",
    },
    { reset, args: me },
  );

  const { mutate, isLoading } = api.patient.profileSave.useMutation({
    onSuccess: () => {
      void apiUtils.patient.profile.invalidate();
      void apiUtils.context.person.invalidate();
      updateState();
    },
  });

  const isDisabled = !formState.isDirty && formState.isValid;

  const onSubmit: SubmitHandler<ProfileFormType> = (values) => {
    mutate(values);
  };

  return (
    <View data-id={"profile-container"}>
      <Card
        data-id={"profile-card"}
        gap={"$5"}
        maxWidth={659}
        variant={sm ? "borderless" : "default"}
        shadow={sm ? "none" : "md"}
        $sm={{
          paddingHorizontal: "$3",
          paddingTop: "$3",
          paddingBottom: "$5",
        }}
        $gtSm={{
          paddingHorizontal: "$12",
          paddingVertical: "$6",
        }}
      >
        <View
          gap={"$1"}
          $sm={{
            gap: "$1.2",
          }}
        >
          <Card.Title
            data-id={"profile-title"}
            width={"100%"}
            textAlign={"left"}
            $gtSm={{ textAlign: "center" }}
          >
            Привіт, {me.name}!
          </Card.Title>
          <Text
            data-id={"profile-subtitle"}
            fontFamily={"$subtitle"}
            fontWeight={"$4"}
            color="$gray-650"
            fontSize="$6"
            lineHeight="$6"
            textAlign="center"
            $sm={{
              fontFamily: "$subtitle",
              fontSize: "$3",
              lineHeight: "$3",
              textAlign: "left",
            }}
          >
            Тут ви можете змінити ваші дані:
          </Text>
        </View>

        <Form
          gap={"$7.8"}
          $sm={{ gap: "$5" }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <View
            flexShrink={1}
            width={"100%"}
            $sm={{
              gap: "$2",
            }}
            gap={"$3"}
          >
            <Controller
              key={"name"}
              control={control}
              name={"name"}
              render={({ field }) => (
                <InputWithLabel
                  data-id={"profile-name-input"}
                  maxWidth={467}
                  label={"Ім’я"}
                  placeholder={"Ім’я"}
                  variant="inline"
                  size="md"
                  {...field}
                />
              )}
            />

            {config.map((item) => (
              <Controller
                key={item.name}
                control={control}
                name={item.name as keyof ProfileFormType}
                render={({ field }) => (
                  <Select
                    dataId={`profile-${item.name}-select`}
                    value={field.value}
                    trigger={item.placeholder}
                    label={item.placeholder}
                    maxWidth={467}
                    items={item.selectItems}
                    onValueChange={(value) => field.onChange(value)}
                  />
                )}
              />
            ))}
          </View>

          <XStack gap={"$2"} width={"100%"}>
            <Link data-id={"profile-back-link"} href={"/"} asChild>
              <Button
                variant="gray"
                rounded="lg"
                size="lg"
                mode={"icon"}
                {...(sm && {
                  size: "md",
                })}
              >
                <ArrowLeft />
              </Button>
            </Link>
            <Form.Trigger asChild>
              <Button
                data-id={"profile-save-button"}
                disabled={isDisabled || isLoading}
                width={"100%"}
                flex={1}
                size="lg"
                variant="primary"
                rounded="lg"
                {...(sm && {
                  size: "md",
                })}
              >
                {isLoading ? "Зберігаємо..." : currentState}
              </Button>
            </Form.Trigger>
          </XStack>
        </Form>
      </Card>
    </View>
  );
};
